/**
 * Every React Query key the api modules share, in one module that imports
 * nothing.
 *
 * The api modules form import cycles through the `@/hooks` barrel, so a key
 * read at module-evaluation time from a peer can land as `undefined` —
 * `invalidateQueries({ queryKey: undefined })` matches every query in the
 * cache rather than none. A module with no imports of its own is always fully
 * evaluated before anyone reads it.
 */

/**
 * What one segment of a query key may be.
 *
 * Narrow on purpose: a key constant here is already an array, so a caller
 * writing `[[SOME_KEY]]` where `[SOME_KEY]` was meant names a key whose first
 * segment is an array. React Query matches segment by segment, so such a key
 * matches no query and its invalidation is a silent no-op.
 */
export type QueryKeySegment = string | number | boolean | null | undefined

/** The keys one mutation busts. */
export type QueryKeyList = ReadonlyArray<readonly QueryKeySegment[]>

// Prefix key — used as invalidation key by all CMS mutations. React Query's
// default prefix match invalidates every scoped variant.
export const CONFIGURATOR_TREE_KEY = ['configurator-tree']
export const CONFIGURATOR_CMS_QUESTIONS_KEY = ['configurator-cms-questions']
export const PRODUCT_CLASSIFICATIONS_KEY = ['product-classifications']
export const CONFIGURATOR_CMS_FORMULAS_KEY = ['configurator-cms-formulas']

// The axes a product is described by. The definitions are one list; a group's
// use of them hangs under the group, and a product's values under the product.
export const PRODUCT_PROPERTIES_KEY = ['product-properties']
export const GROUP_PROPERTIES_KEY = ['group-properties']
export const PRODUCT_PROPERTY_VALUES_KEY = ['product-property-values']
export const MEASURE_PROPERTY_SPECS_KEY = ['measure-property-specs']
/** The ways a product may qualify for a measure, with their match counts. */
export const MEASURE_REQUIREMENTS_KEY = ['measure-requirements']
export const MEASURE_PRODUCT_GROUPS_KEY = ['measure-product-groups']
export const PRODUCT_SUGGESTIONS_KEY = ['product-suggestions']
/** What a pass over the whole catalogue would weigh, and whether one is going. */
export const SUGGESTION_PASS_KEY = ['suggestion-pass']

// The catalogue. The tree key is suffixed with the kind at the call
// site so switching branches doesn't refetch the one already loaded.
export const CATALOGUE_TREE_KEY = ['catalogue-tree']
export const CATALOGUE_MEASURE_KEY = ['catalogue-measure']
/** What every measure charges, whole — its own key, because the branch beside
 *  it carries only what each one comes to. */
export const MEASURE_STAGES_KEY = ['catalogue-measure-stages']
/** The blocks, groups and stage templates a calculation is built from. */
export const CALCULATION_LIBRARY_KEY = ['calculation-library']
/** What charges one block or one group, read per pane rather than carried on
 *  the branch — see `referencedBy`. */
export const CALCULATION_USAGE_KEY = ['calculation-usage']
export const CATALOGUE_PRODUCT_KEY = ['catalogue-product']
export const CATALOGUE_LABOUR_KEY = ['catalogue-labour-item']
export const CATALOGUE_FORMULA_USAGE_KEY = ['catalogue-formula-usage']
/** A thread's own root: writing a note moves no figure, so it must not drop
 *  the branch the pane was opened from. */
export const CATALOGUE_COMMENTS_KEY = 'catalogue-entry-comments'
export const AI_CONFIG_KEY = ['ai-config']

export const HISTORY_KEY = ['change-history']
/** One project's configurator answers, as they were changed over time. */
export const CONFIGURATOR_HISTORY_KEY = (projectId: number) => [
  'configurator-history',
  projectId,
]
/**
 * One record's own key, and the list its siblings are filed under.
 *
 * The `detail` and `list` segments are what let a write drop every record of
 * an entity without touching the lists under the same prefix — a cost entry
 * changes a project's positions, not which projects exist.
 */
export const detailKey = (
  entityKey: readonly QueryKeySegment[],
  id: QueryKeySegment,
): readonly QueryKeySegment[] => [...entityKey, 'detail', id]

export const listKey = (
  entityKey: readonly QueryKeySegment[],
  params?: unknown,
): readonly unknown[] =>
  params ? [...entityKey, 'list', params] : [...entityKey, 'list']

/** The two entities `useApi` serves. Written out at eighteen call sites before
 *  they had a name. */
export const PROJECTS_KEY = ['projects']

/** The customers a project names, for the list filter. Under the projects
 *  prefix, so `useApi` has to name it to reach it. */
export const PROJECT_CUSTOMERS_KEY = [...PROJECTS_KEY, 'customers']
export const USERS_KEY = ['users']

// The media library. Both halves sit under the prefix, so a write that
// changes what the library holds reaches them together — and apart, so a write
// that touches one row can refresh the list without dropping every cached
// detail with it.
export const MEDIA_ASSETS_KEY = ['media-assets']
export const MEDIA_ASSETS_LIST_KEY = listKey(MEDIA_ASSETS_KEY)
export const MEDIA_ASSET_KEY = (id: number | null) =>
  detailKey(MEDIA_ASSETS_KEY, id)
export const MEDIA_ASSETS_TAGS_KEY = ['media-assets-tags']
export const MEDIA_COLLECTIONS_KEY = ['media-collections']
export const IMAGE_USAGES_KEY = ['image-usages']
export const MEDIA_REPROCESS_KEY = ['media-reprocess']
// The matching side: a mandate's own list is keyed under the mandate key, so
// invalidating the prefix reaches the lists as well as the mandates.
export const SEARCH_MANDATES_KEY = ['search-mandates']
export const PROPERTIES_KEY = ['properties']
export const EMAIL_STATS_KEY = ['email-stats']
export const STALE_MATCH_COUNT_KEY = ['matching', 'stale-matches', 'count']
export const CUSTOMER_MANDATES_KEY = ['customer-mandates']

export const CUSTOMER_PROJECTS_KEY = ['customer-projects']
export const CUSTOMER_PROJECT_KEY = ['customer-project']
export const CUSTOMER_PROJECT_ADVISOR_KEY = ['customer-project-advisor']
export const CUSTOMER_SELECTIONS_KEY = ['customer-selections']
export const CUSTOMER_POSITIONS_KEY = ['customer-positions']
export const PUBLIC_HOUSE_ELEMENT_TYPES_KEY = ['public-house-element-types']
export const PUBLIC_CONFIGURATOR_TREE_KEY = [
  ...CONFIGURATOR_TREE_KEY,
  'public-endpoint',
]

/** Prefix key — a write that changes the property invalidates through this,
 *  reaching every project's cached response. */
export const PLANNING_SELECTIONS_PREFIX = ['planning-selections']

export const PLANNING_SELECTIONS_KEY = (projectId: number) =>
  [...PLANNING_SELECTIONS_PREFIX, projectId] as const

/** Every mandate as a picker names it. Outside `SEARCH_MANDATES_KEY` because
 *  a match write busts that prefix and cannot rename a customer. */
export const MANDATE_NAMES_KEY = ['mandate-names'] as const

/** Everybody a project may name for a post or be given to — the roster the
 *  staffing rows and the access list are both picked from. */
export const PROJECT_STAFF_KEY = ['project-staff'] as const

/**
 * How long a roster behind a picker stays fresh. It changes when somebody is
 * hired or a project changes hands, not while a list is being read — without
 * it every window refocus refetches one.
 */
export const ROSTER_STALE_TIME = 5 * 60 * 1000
